// Dashed drop-tile for one uploaded asset (image, video or audio clip) used by
// the MiniMax H3 page for boundary frames and reference assets. Uploads to
// the feature's /upload endpoint (multipart `file` + `kind`) and shows an
// inline preview with a remove affordance. Sibling of FrameInput, which is
// the older image-only picker.

import { useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import AddPhotoIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import MovieIcon from '@mui/icons-material/MovieOutlined';
import AudiotrackIcon from '@mui/icons-material/AudiotrackOutlined';
import CloseIcon from '@mui/icons-material/CloseRounded';
import type { UploadResult } from './FrameInput';

export type AssetKind = 'image' | 'video' | 'audio';

const ACCEPT: Record<AssetKind, string> = {
  image: 'image/jpeg,image/png,image/webp',
  video: 'video/mp4,video/quicktime,video/webm,video/x-matroska',
  audio: 'audio/wav,audio/x-wav,audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/flac',
};

export async function uploadAsset(uploadUrl: string, kind: AssetKind, file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  const res = await fetch(uploadUrl, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${body}`);
  }
  return (await res.json()) as UploadResult;
}

export function AssetInput({
  uploadUrl,
  kind,
  label,
  badge,
  badgeLabel,
  value,
  onChange,
  onClear,
  aspectRatio = '4 / 3',
  onError,
}: {
  uploadUrl: string;
  kind: AssetKind;
  label: string;
  // Corner pill like the reference design: pink "Required", grey "Optional".
  badge?: 'required' | 'optional';
  badgeLabel?: string;
  value: UploadResult | null;
  onChange: (v: UploadResult) => void;
  onClear: () => void;
  aspectRatio?: string;
  onError?: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const Icon = kind === 'image' ? AddPhotoIcon : kind === 'video' ? MovieIcon : AudiotrackIcon;

  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadAsset(uploadUrl, kind, f));
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const filename = value ? value.r2_key.split('/').slice(-1)[0] : '';

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px dashed',
        borderColor: value ? 'transparent' : 'divider',
        bgcolor: 'action.selected',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: value ? 'transparent' : 'primary.main', bgcolor: value ? 'action.selected' : 'action.hover' },
      }}
    >
      {value ? (
        <>
          {kind === 'image' && (
            <Box component="img" src={value.url} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          {kind === 'video' && (
            <Box
              component="video"
              src={value.url}
              muted
              playsInline
              controls
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', bgcolor: 'black' }}
            />
          )}
          {kind === 'audio' && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, px: 1.5 }}>
              <AudiotrackIcon sx={{ color: 'primary.light' }} />
              <Typography variant="caption" noWrap sx={{ maxWidth: '100%', color: 'text.primary' }}>
                {filename}
              </Typography>
              <Box component="audio" src={value.url} controls preload="none" sx={{ width: '100%', height: 32 }} />
            </Box>
          )}
          <Tooltip title="Remove" arrow>
            <IconButton
              size="small"
              aria-label={`remove ${label}`}
              onClick={onClear}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'common.white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Box
          component="label"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            cursor: busy ? 'progress' : 'pointer',
            color: 'text.secondary',
            textAlign: 'center',
            px: 1.5,
          }}
        >
          {busy ? <CircularProgress size={22} /> : <Icon sx={{ fontSize: 28, color: 'text.primary' }} />}
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {busy ? 'Uploading…' : label}
          </Typography>
          <input hidden type="file" accept={ACCEPT[kind]} onChange={handle} disabled={busy} aria-label={label} />
        </Box>
      )}
      {badge && !value && (
        <Chip
          size="small"
          label={badgeLabel ?? (badge === 'required' ? 'Required' : 'Optional')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            pointerEvents: 'none',
            bgcolor: badge === 'required' ? '#e879a8' : 'rgba(255,255,255,0.12)',
            color: badge === 'required' ? '#1a0a12' : 'text.primary',
          }}
        />
      )}
    </Box>
  );
}
